/**
 * post_create 发布体构建
 *
 * 字段结构以真实浏览器请求为准（见同目录 real.js，以及
 * scripts/api-explorer/shipinhao-video-upload/dist/network-logs1.json 里捕获的 post_create 请求体）。
 *
 * 两次独立抓包一致的关键点：
 * - 顶层 latitude/longitude/feedLatitude/feedLongitude 恒为 0，真实坐标只写在 objectDesc.location
 * - objectDesc.shortTitle 是数组 `[{ shortTitle }]`，不是字符串
 * - media[].md5sum 填 UUID 而非真实 md5（服务端不校验）
 * - report 里的 width/height/duration/fileSize 是转码**前**的原始视频信息
 */

import type { ShipinhaoPublishVideoParams } from "./index";

/**
 * @用户在 description / mentionedUser 里的尾随分隔符：普通空格 U+0020
 *
 * 与 XML CDATA 里用的 U+2005 不同，两处实测就是两个不同字符，不能统一。
 */
const MENTION_TEXT_SUFFIX = "\u0020";

/**
 * @用户在 finderTopicInfo CDATA 里的尾随分隔符：四分之一空格 U+2005
 *
 * 前端编辑器插入 @提及时用的是这个不可断空格，服务端按它切分 @用户片段。
 */
const MENTION_XML_SUFFIX = "\u2005";

/** 描述文案中的话题片段：正文 → 话题 → @用户，与前端插入顺序一致 */
export function buildDescription(params: {
    description: string;
    topics?: string[];
    mentionedUsers?: Array<{ nickname: string }>;
}): string {
    let description = params.description || "";

    for (const topic of params.topics || []) {
        description += `#${topic}`;
    }

    for (const user of params.mentionedUsers || []) {
        description += `@${user.nickname}${MENTION_TEXT_SUFFIX}`;
    }

    return description;
}

/** mentionedUser[].nickname：实测昵称后带一个普通空格 */
export function buildMentionedUser(mentionedUsers?: Array<{ nickname: string }>): Array<{ nickname: string }> {
    return (mentionedUsers || []).map((user) => ({ nickname: `${user.nickname}${MENTION_TEXT_SUFFIX}` }));
}

/**
 * 构建 finderTopicInfo XML
 *
 * value 节点按其在文案中出现的顺序排列：正文（非空时）为纯 CDATA，
 * 话题为 `<topic><![CDATA[#name#]]></topic>`（注意首尾都有 #），
 * @用户为 `@nickname` + U+2005 的 CDATA。
 * `<style><at>` 指向第一个 @用户所在的 value 序号，没有 @用户时为空。
 *
 * ⚠️ `description` 传的必须是**原始正文**，不是 buildDescription 拼接后的结果 ——
 * 拼接结果里已经含话题与 @用户，再进 value0 会重复一遍。
 */
export function buildTopicXml(params: {
    description: string;
    topics?: string[];
    mentionedUsers?: Array<{ nickname: string }>;
}): string {
    const values: string[] = [];
    let atIndex: number | null = null;

    if (params.description) {
        values.push(`<![CDATA[${params.description}]]>`);
    }

    for (const topic of params.topics || []) {
        values.push(`<topic><![CDATA[#${topic}#]]></topic>`);
    }

    for (const user of params.mentionedUsers || []) {
        if (atIndex === null) atIndex = values.length;
        values.push(`<![CDATA[@${user.nickname}${MENTION_XML_SUFFIX}]]>`);
    }

    let xml = "<finder>";
    xml += "<version>1</version>";
    xml += `<valuecount>${values.length}</valuecount>`;
    xml += `<style><at>${atIndex ?? ""}</at></style>`;
    values.forEach((value, index) => {
        xml += `<value${index}>${value}</value${index}>`;
    });
    xml += "</finder>";

    return xml;
}

/** objectDesc.location：无位置时下发全 0 占位，与前端一致 */
export function buildLocation(location?: ShipinhaoPublishVideoParams["location"]): Record<string, unknown> {
    if (!location) {
        return { latitude: 0, longitude: 0, city: "", poiName: "", address: "", poiClassifyId: "" };
    }

    return {
        latitude: location.latitude,
        longitude: location.longitude,
        city: location.city,
        poiName: location.poiName || "",
        address: location.address || "",
        poiClassifyId: location.poiClassifyId || "",
    };
}

/**
 * objectDesc.topic：话题 XML + 合集信息合并在同一对象里
 *
 * `description` 同样要传原始正文，见 buildTopicXml 的说明。
 */
export function buildTopic(params: {
    description: string;
    topics?: string[];
    mentionedUsers?: Array<{ nickname: string }>;
    collection?: ShipinhaoPublishVideoParams["collection"];
}): Record<string, unknown> {
    const topic: Record<string, unknown> = {
        finderTopicInfo: buildTopicXml(params),
    };

    if (params.collection) {
        topic.collectionId = params.collection.collectionId;
        topic.collectionName = params.collection.collectionName;
    }

    return topic;
}

/** objectDesc.event：未参与活动时为空对象 */
export function buildEvent(event?: ShipinhaoPublishVideoParams["event"]): Record<string, unknown> {
    if (!event) return {};

    return {
        eventTopicId: event.eventTopicId,
        eventName: event.eventName,
        eventCreatorNickname: event.eventCreatorNickname || "",
    };
}

/** objectDesc.extReading：无链接时也要带全字段占位（实测前端行为） */
export function buildExtReading(link?: ShipinhaoPublishVideoParams["link"]): Record<string, unknown> {
    if (!link) return { link: "", title: "", urlType: 1 };

    return {
        // 链接里的空格与零宽字符会导致校验失败，与图文发布保持同样的清洗
        link: link.link.replace(/[\s\u200b]/g, ""),
        title: link.title,
        urlType: link.urlType ?? 1,
    };
}

/**
 * 顶层 tagInfo：原样透传调用方传入的对象，只补上实时 tagKey
 *
 * tagKey 由服务端每次下发且带时效，必须用当次取到的值，所以这里始终覆盖调用方传的同名字段。
 */
export function buildTagInfo(
    tagInfo: NonNullable<ShipinhaoPublishVideoParams["tagInfo"]>,
    tagKey: string,
): Record<string, unknown> {
    return { ...tagInfo, tagKey };
}
